// shadcn/ui's Radix Select composition, adapted to Valid Labels tokens.
// https://ui.shadcn.com/docs/components/radix/select
import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/utils";
import { useFieldProps } from "./input";

// Keep the existing explicit empty choice (e.g. "Choose a printer") selectable.
// Radix reserves the empty string for clearing its value.
const EMPTY_VALUE = "__vl_empty_choice__";
type SelectProps = Omit<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
  "value" | "defaultValue" | "onChange"
> & {
  value: string | number;
  onValueChange: (value: string) => void;
  name?: string;
  required?: boolean;
};

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      children,
      className,
      value,
      onValueChange,
      name,
      required,
      disabled,
      ...props
    },
    ref,
  ) => {
    const field = useFieldProps(props);
    const [content, setContent] = React.useState<HTMLDivElement | null>(null);
    const restoreBackground = React.useRef(() => {});
    React.useLayoutEffect(() => {
      // Radix hides the background from assistive technology and traps focus.
      // Inert also prevents hidden controls receiving browser/keyboard focus.
      if (!content) return;
      const background = Array.from(document.body.children).filter(
        (element): element is HTMLElement =>
          element instanceof HTMLElement &&
          !element.contains(content) &&
          !element.inert,
      );
      background.forEach((element) => {
        element.inert = true;
      });
      const restore = () => {
        background.forEach((element) => {
          element.inert = false;
        });
        restoreBackground.current = () => {};
      };
      restoreBackground.current = restore;
      return restore;
    }, [content]);
    return (
      <SelectPrimitive.Root
        value={value === "" ? EMPTY_VALUE : String(value)}
        onValueChange={(next) =>
          onValueChange(next === EMPTY_VALUE ? "" : next)
        }
        name={name}
        required={required}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          className={cn("vl-input vl-select", className)}
          {...props}
          {...field}
        >
          <SelectPrimitive.Value />
          <SelectPrimitive.Icon asChild>
            <ChevronDown
              aria-hidden="true"
              className="vl-select-chevron"
              size={14}
            />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            ref={setContent}
            onCloseAutoFocus={() => restoreBackground.current()}
            className="vl-select-content"
            position="popper"
            sideOffset={4}
            collisionPadding={12}
          >
            <SelectPrimitive.ScrollUpButton className="vl-select-scroll">
              <ChevronUp aria-hidden="true" size={14} />
            </SelectPrimitive.ScrollUpButton>
            <SelectPrimitive.Viewport
              className="vl-select-viewport"
              role="group"
              tabIndex={0}
            >
              {children}
            </SelectPrimitive.Viewport>
            <SelectPrimitive.ScrollDownButton className="vl-select-scroll">
              <ChevronDown aria-hidden="true" size={14} />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    );
  },
);
Select.displayName = "Select";

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, value, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn("vl-select-item", className)}
    value={value === "" ? EMPTY_VALUE : value}
    data-value={value}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="vl-select-indicator">
      <Check aria-hidden="true" size={14} />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItem.displayName = "SelectItem";
